/**
 * SecurityEditFormBody — shared form body for CREATE / ALTER {LOGIN|USER|ROLE}.
 *
 * Engines:
 *   - mssql:    LOGIN + USER + ROLE all supported (server + db principals).
 *   - postgres: ROLE only (collapses logins / users into roles via LOGIN attr).
 *   - mysql:    USER + ROLE; no server-level LOGIN.
 *   - oracle:   USER + ROLE.
 *   - sqlite:   unsupported — surface disabled with documented tooltip.
 *
 * Modes:
 *   - `create`: builds Login/User/Role Def, dispatches commitCreate{Login|User|Role}.
 *   - `alter`:  builds the matching Change object, dispatches commitAlter{Login|User|Role}.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice F1.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import {
  commitAlterLogin,
  isAlterLoginSupported,
  validateAlterLoginForEngine,
} from '../../edit/edits/alterLogin';
import {
  commitAlterRole,
  isAlterRoleSupported,
  validateAlterRoleForEngine,
} from '../../edit/edits/alterRole';
import {
  commitAlterUser,
  isAlterUserSupported,
  validateAlterUserForEngine,
} from '../../edit/edits/alterUser';
import {
  commitCreateLogin,
  isCreateLoginSupported,
  validateCreateLoginForEngine,
} from '../../edit/edits/createLogin';
import {
  commitCreateRole,
  isCreateRoleSupported,
  validateCreateRoleForEngine,
} from '../../edit/edits/createRole';
import {
  commitCreateUser,
  isCreateUserSupported,
  validateCreateUserForEngine,
} from '../../edit/edits/createUser';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { SecurityFormFields } from './SecurityFormFields';
import {
  buildLoginChange,
  buildLoginDef,
  buildRoleChange,
  buildRoleDef,
  buildUserChange,
  buildUserDef,
  DEFAULT_SECURITY_FORM_VALUES,
  getKindUnsupportedTooltip,
  isKindSupportedForEngine,
  type SecurityEditMode,
  type SecurityFormValues,
} from './securityFormHelpers';
import { FormActionsSlot } from './FormActionsSlot';
import { FormEngineChip } from './FormEngineChip';

export type { SecurityEditMode, SecurityFormValues } from './securityFormHelpers';

export interface SecurityEditFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly mode: SecurityEditMode;
  readonly initialValues?: Partial<SecurityFormValues>;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function SecurityEditFormBody(props: SecurityEditFormBodyProps) {
  const { editContext, mode, initialValues, testId, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;

  const [values, setValues] = useState<SecurityFormValues>({
    ...DEFAULT_SECURITY_FORM_VALUES, ...initialValues,
  });
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (engine === 'sqlite') {
    return renderUnsupported(
      testId,
      'SQLite has no auth model — connection-level access is OS / file-permission driven.',
      onCancel,
    );
  }
  if (!isKindSupportedForEngine(engine, values.kind)) {
    return renderUnsupported(
      testId,
      getKindUnsupportedTooltip(engine, values.kind),
      onCancel,
    );
  }

  async function handleSubmit() {
    setError(null);
    if (mode === 'create') return commitCreate();
    return commitAlter();
  }

  async function commitCreate() {
    if (values.kind === 'login') {
      if (!isCreateLoginSupported(engine)) {
        setError('LOGIN is mssql-only.');
        return;
      }
      const login = buildLoginDef(values);
      const v = validateCreateLoginForEngine(engine, { login });
      if (v) { setError(v); return; }
      setPending(true);
      try { await commitCreateLogin(editContext, { login }); onCommitted?.(); }
      catch (e) { setError(toMessage(e)); setPending(false); }
      return;
    }
    if (values.kind === 'user') {
      if (!isCreateUserSupported(engine)) {
        setError(getKindUnsupportedTooltip(engine, 'user'));
        return;
      }
      const user = buildUserDef(engine, values);
      const v = validateCreateUserForEngine(engine, { user });
      if (v) { setError(v); return; }
      setPending(true);
      try { await commitCreateUser(editContext, { user }); onCommitted?.(); }
      catch (e) { setError(toMessage(e)); setPending(false); }
      return;
    }
    if (!isCreateRoleSupported(engine)) {
      setError(getKindUnsupportedTooltip(engine, 'role'));
      return;
    }
    const role = buildRoleDef(engine, values);
    const v = validateCreateRoleForEngine(engine, { role });
    if (v) { setError(v); return; }
    setPending(true);
    try { await commitCreateRole(editContext, { role }); onCommitted?.(); }
    catch (e) { setError(toMessage(e)); setPending(false); }
  }

  async function commitAlter() {
    if (values.kind === 'login') {
      if (!isAlterLoginSupported(engine)) {
        setError('LOGIN is mssql-only.');
        return;
      }
      const change = buildLoginChange(values);
      const args = { loginName: values.name, change };
      const v = validateAlterLoginForEngine(engine, args);
      if (v) { setError(v); return; }
      setPending(true);
      try { await commitAlterLogin(editContext, args); onCommitted?.(); }
      catch (e) { setError(toMessage(e)); setPending(false); }
      return;
    }
    if (values.kind === 'user') {
      if (!isAlterUserSupported(engine)) {
        setError(getKindUnsupportedTooltip(engine, 'user'));
        return;
      }
      const change = buildUserChange(engine, values);
      const args = {
        userName: values.name,
        change,
        ...(values.mysqlHost ? { mysqlHost: values.mysqlHost } : {}),
      };
      const v = validateAlterUserForEngine(engine, args);
      if (v) { setError(v); return; }
      setPending(true);
      try { await commitAlterUser(editContext, args); onCommitted?.(); }
      catch (e) { setError(toMessage(e)); setPending(false); }
      return;
    }
    if (!isAlterRoleSupported(engine)) {
      setError(getKindUnsupportedTooltip(engine, 'role'));
      return;
    }
    const change = buildRoleChange(engine, values);
    const args = { roleName: values.name, change };
    const v = validateAlterRoleForEngine(engine, args);
    if (v) { setError(v); return; }
    setPending(true);
    try { await commitAlterRole(editContext, args); onCommitted?.(); }
    catch (e) { setError(toMessage(e)); setPending(false); }
  }

  const submitLabel = pending
    ? 'Saving…'
    : mode === 'create'
      ? `Create ${values.kind}`
      : `Alter ${values.kind}`;

  return (
    <div data-testid={testId} className="flex flex-col gap-3 max-h-[60vh] overflow-y-auto pr-1">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">
          {mode === 'create' ? 'Create security principal' : 'Alter security principal'}
        </div>
        <FormEngineChip engine={engine} />
      </div>

      <SecurityFormFields
        engine={engine}
        mode={mode}
        values={values}
        setValues={setValues}
        testId={testId}
      />

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <FormActionsSlot className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onCancel?.()}
          data-testid={`${testId}-cancel`}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={pending}
          onClick={() => void handleSubmit()}
          data-testid={`${testId}-submit`}
        >
          {submitLabel}
        </Button>
      </FormActionsSlot>
    </div>
  );
}

function toMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function renderUnsupported(testId: string, message: string, onCancel?: () => void) {
  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="text-xs font-semibold">Security</div>
      <div
        className="rounded-md border border-border bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
        data-testid={`${testId}-unsupported`}
      >
        {message}
      </div>
      <div className="flex items-center justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onCancel?.()}
          data-testid={`${testId}-close`}
        >
          Close
        </Button>
      </div>
    </div>
  );
}
